import { useRef, useState } from "react";
import { ToggleGroup, ToggleGroupItem } from "../../shadcn/toggle-group";

const MORE = "more";

export type MinutesChipsProps = {
  label: string;
  options: { value: number; label: string }[];
  value: number | null;
  onChange(n: number): void;
  moreLabel?: string;
  /** Lowest number "More" accepts; 1 where zero would contradict the answer above it. */
  min?: number;
};

/** One-tap minutes; "More" takes any whole number the server accepts (min..720, A8). */
export function MinutesChips({ label, options, value, onChange, moreLabel = "More", min = 0 }: MinutesChipsProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState(false);
  // Once the field is closed (Enter or Escape), a blur that follows must not commit again.
  const closed = useRef(false);

  const rangeError = `Enter whole minutes from ${min} to 720.`;
  const custom = value !== null && !options.some((o) => o.value === value);
  const selected = value === null ? "" : custom ? MORE : String(value);

  const startEditing = () => {
    setText(value === null ? "" : String(value));
    setError(false);
    closed.current = false;
    setEditing(true);
  };

  const commit = () => {
    if (closed.current) return;
    const n = /^\d+$/.test(text.trim()) ? Number(text.trim()) : NaN;
    if (!Number.isInteger(n) || n < min || n > 720) {
      setError(true);
      return;
    }
    closed.current = true;
    setEditing(false);
    setError(false);
    onChange(n);
  };

  return (
    <div>
      <ToggleGroup
        type="single"
        variant="chip"
        aria-label={label}
        className="gap-1"
        value={selected}
        onValueChange={(v) => {
          // Radix sends "" when the selected chip is clicked again; More is handled by its click.
          if (!v || v === MORE) return;
          setEditing(false);
          onChange(Number(v));
        }}
      >
        {options.map((o) => (
          <ToggleGroupItem key={o.value} value={String(o.value)}>
            {o.label}
          </ToggleGroupItem>
        ))}
        <ToggleGroupItem value={MORE} onClick={startEditing}>
          {custom ? `${value} min` : moreLabel}
        </ToggleGroupItem>
      </ToggleGroup>
      {editing && (
        <div className="mt-2 flex items-center gap-2 text-[13px]">
          <input
            autoFocus
            inputMode="numeric"
            aria-label={`${label}, minutes`}
            aria-invalid={error}
            className="h-8 w-20 rounded-lg border border-neutral-200 px-3 outline-none transition duration-150 ease-soft focus:border-brand focus:shadow-ring aria-[invalid=true]:border-red-600"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                closed.current = true;
                setEditing(false);
                setError(false);
              }
            }}
            onBlur={commit}
          />
          <span className="text-neutral-500">min</span>
        </div>
      )}
      {editing && error && <p className="mt-1 text-[13px] text-red-600">{rangeError}</p>}
    </div>
  );
}
